import React, { useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { X, User, BadgeCheck } from "lucide-react";

import GuarantorPhoneNumberInput from "../GuarantorPhoneInput/index";

export interface GuarantorFormData {
    id: string;
    name: string;
    phone: string;
    role: string;
}

type Errors = { name?: string; phone?: string; role?: string };

type Props = {
    open: boolean;
    onClose: () => void;
    onSave: (guarantor: GuarantorFormData) => void;
    initialData?: GuarantorFormData;
};

function isValidCyrillicName(value: string) {
    return /^[А-Яа-яЁё\s]{2,}$/.test(value.trim());
}

const GuarantorPopup: React.FC<Props> = ({ open, onClose, onSave, initialData }) => {
    const [guarantorData, setGuarantorData] = useState<GuarantorFormData>({
        id: uuidv4(),
        name: "",
        phone: "996",
        role: "",
    });

    const [errors, setErrors] = useState<Errors>({});

    const isEdit = useMemo(() => Boolean(initialData), [initialData]);

    useEffect(() => {
        if (!open) return;

        setErrors({});
        if (initialData) {
            setGuarantorData({
                ...initialData,
                phone: initialData.phone && initialData.phone.trim() ? initialData.phone : "996",
            });
        } else {
            setGuarantorData({ id: uuidv4(), name: "", phone: "996", role: "" });
        }
    }, [open, initialData]);

    const setField =
        (field: keyof GuarantorFormData) =>
            (e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                setGuarantorData((prev) => ({ ...prev, [field]: value }));

                // мягко чистим ошибку при вводе
                setErrors((prev) => ({ ...prev, [field]: undefined }));
            };

    const handlePhoneChange = (value: string) => {
        setGuarantorData((prev) => ({ ...prev, phone: value }));
        setErrors((prev) => ({ ...prev, phone: undefined }));
    };

    const validate = (): Errors => {
        const next: Errors = {};

        if (!isValidCyrillicName(guarantorData.name)) {
            next.name = "ФИО должно содержать минимум 2 буквы";
        }

        if (!isValidCyrillicName(guarantorData.role)) {
            next.role = "Поле 'Кем является' должно содержать минимум 2 буквы";
        }

        // Если хочешь валидацию телефона, добавим сюда (пока оставил как было: без regexp).
        // if (!/^\d{12,15}$/.test(guarantorData.phone.replace(/\D/g, ""))) next.phone = "Некорректный номер";

        return next;
    };

    const handleSave = () => {
        const v = validate();
        if (Object.keys(v).length) {
            setErrors(v);
            return;
        }
        onSave(guarantorData);
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={(v) => (!v ? onClose() : null)}>
            <DialogContent className="sm:max-w-[520px] p-0 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b">
                    <DialogHeader className="p-0">
                        <DialogTitle className="text-base">
                            {isEdit ? "Редактировать поручителя" : "Добавить поручителя"}
                        </DialogTitle>
                    </DialogHeader>

                    {/*<Button type="button" variant="ghost" size="icon" onClick={onClose} className="h-9 w-9">*/}
                    {/*    <X className="h-4 w-4" />*/}
                    {/*</Button>*/}
                </div>

                <div className="px-4 py-4 space-y-4">
                    {/* ФИО */}
                    <div className="space-y-2">
                        <Label htmlFor="guarantor-name" className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            ФИО
                        </Label>
                        <Input
                            id="guarantor-name"
                            value={guarantorData.name}
                            onChange={setField("name")}
                            placeholder="Например: Айбек уулу Нурбек"
                            className={errors.name ? "border-destructive focus-visible:ring-destructive" : ""}
                        />
                        {errors.name ? <p className="text-xs text-destructive">{errors.name}</p> : null}
                    </div>

                    {/* Телефон */}
                    <div className="space-y-2">
                        <Label className="flex items-center gap-2">Телефон</Label>

                        {/* твой компонент, просто в красивой обертке */}
                        <div
                            className={[
                                "rounded-md border px-3 py-2",
                                errors.phone ? "border-destructive" : "border-input",
                            ].join(" ")}
                        >
                            <GuarantorPhoneNumberInput
                                value={guarantorData.phone}
                                onChange={handlePhoneChange}
                                error={errors.phone}
                            />
                        </div>

                        {errors.phone ? <p className="text-xs text-destructive">{errors.phone}</p> : null}
                    </div>

                    {/* Кем является */}
                    <div className="space-y-2">
                        <Label htmlFor="guarantor-role" className="flex items-center gap-2">
                            <BadgeCheck className="h-4 w-4 text-muted-foreground" />
                            Кем является
                        </Label>
                        <Input
                            id="guarantor-role"
                            value={guarantorData.role}
                            onChange={setField("role")}
                            placeholder="Например: отец, мама, брат"
                            className={errors.role ? "border-destructive focus-visible:ring-destructive" : ""}
                        />
                        {errors.role ? <p className="text-xs text-destructive">{errors.role}</p> : null}
                    </div>
                </div>

                <DialogFooter className="px-4 py-3 border-t flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">
                        Отмена
                    </Button>
                    <Button type="button" onClick={handleSave} className="w-full sm:w-auto">
                        Сохранить
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default GuarantorPopup;
